import { ChevronRight } from 'lucide-react'
import { Fragment, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { getUsageTypeColor } from '@/routes/usage/constants'
import { formatMoney } from '@/routes/usage/utils'
import type { UsageGroup } from '@/routes/usage/types'

type UsageTableProps = {
  groups: UsageGroup[]
  totalGross: number
  serviceDisplayNameMap: Map<string, string>
  onHighlightType: (type: string | null) => void
}

export const UsageTable = ({
  groups,
  totalGross,
  serviceDisplayNameMap,
  onHighlightType,
}: UsageTableProps) => {
  const visibleGroups = groups.filter((g) => g.gross !== 0)
  const totalNet = visibleGroups.reduce((s, g) => s + g.net, 0)
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set())

  const toggleExpanded = (type: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }

  return (
    <div>
      <div className="rounded-md border mx-6 print:border-0 print:mx-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-[50%] pl-4">Name</TableHead>
              <TableHead className="w-[18%] text-right">Net</TableHead>
              <TableHead className="w-[18%] text-right">Gross</TableHead>
              <TableHead className="w-[14%] text-right pr-4">
                % of total
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleGroups.length > 0 ? (
              visibleGroups.map((group) => {
                const percentage =
                  totalGross > 0 ? (group.gross / totalGross) * 100 : 0
                const isExpanded = expandedTypes.has(group.type)
                const isExpandable = group.rows.length > 0

                return (
                  <Fragment key={group.type}>
                    <TableRow
                      className={cn(
                        isExpandable && 'cursor-pointer',
                        isExpanded && 'border-b-0 print:border-b'
                      )}
                      onClick={() => {
                        if (isExpandable) {
                          toggleExpanded(group.type)
                        }
                      }}
                      onMouseEnter={() => onHighlightType(group.type)}
                      onMouseLeave={() => onHighlightType(null)}
                    >
                      <TableCell className="py-2 pl-4">
                        <div className="flex items-center gap-2 font-medium">
                          {isExpandable && (
                            <ChevronRight
                              className={cn(
                                'size-4 shrink-0 transition-transform print:hidden',
                                isExpanded && 'rotate-90'
                              )}
                            />
                          )}
                          <div
                            className="size-2.5 rounded-full shrink-0 print:hidden"
                            style={{
                              backgroundColor: getUsageTypeColor(group.type),
                            }}
                          />
                          {group.label}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-right tabular-nums">
                        {formatMoney(group.net)}
                      </TableCell>
                      <TableCell className="py-2 text-right tabular-nums">
                        {formatMoney(group.gross)}
                      </TableCell>
                      <TableCell className="py-2 text-right text-muted-foreground tabular-nums pr-4">
                        {percentage.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                    {group.rows.map((row) => {
                      const rowPercentage =
                        totalGross > 0 ? (row.gross / totalGross) * 100 : 0
                      const isDeleted = !serviceDisplayNameMap.has(
                        row.idService
                      )

                      return (
                        <TableRow
                          key={`${group.type}-${row.idService}`}
                          className={cn(
                            'bg-muted/30',
                            !isExpanded && 'hidden print:table-row'
                          )}
                        >
                          <TableCell className="py-2 pl-16 print:pl-12">
                            <span className="text-muted-foreground flex items-center gap-2">
                              {serviceDisplayNameMap.get(row.idService) ||
                                row.serviceName ||
                                row.idService}
                              {isDeleted && (
                                <Badge variant="outline" className="text-xs">
                                  Deleted
                                </Badge>
                              )}
                            </span>
                          </TableCell>
                          <TableCell className="py-2 text-right tabular-nums text-muted-foreground">
                            {formatMoney(row.net)}
                          </TableCell>
                          <TableCell className="py-2 text-right tabular-nums text-muted-foreground">
                            {formatMoney(row.gross)}
                          </TableCell>
                          <TableCell className="py-2 text-right text-muted-foreground tabular-nums pr-4">
                            {rowPercentage.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </Fragment>
                )
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="h-24 text-center text-muted-foreground"
                >
                  No usage matches your filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {visibleGroups.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell className="font-medium pl-4">Total</TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatMoney(totalNet)}
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatMoney(totalGross)}
                </TableCell>
                <TableCell className="text-right text-muted-foreground tabular-nums pr-4">
                  100%
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  )
}
